import type { inferOutput } from "@trpc/tanstack-react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "@tanstack/react-router";
import Columns3Icon from "lucide-react/dist/esm/icons/columns-3";
import GripVerticalIcon from "lucide-react/dist/esm/icons/grip-vertical";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import Settings2Icon from "lucide-react/dist/esm/icons/settings-2";
import XIcon from "lucide-react/dist/esm/icons/x";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Checkbox } from "@quake/shared-ui/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@quake/shared-ui/components/ui/table";
import { toast } from "@quake/shared-ui/lib/sonner";
import { cn } from "@quake/shared-ui/lib/utils";
import {
  getListMemberKey,
  listRecordTypes,
  type ListMemberMoveDestination,
  type ListMemberTableColumn,
  type ListRecordType,
} from "@quake/isomorphic/lists";
import { trpc } from "~/utils/trpc";
import { RecordTypeBadge, entityLabels } from "./lists-ui";

type ListMembersResponse = inferOutput<typeof trpc.lists.listMembers>;
type ListMember = ListMembersResponse["members"][number];
type ListRecordSummary = ListMembersResponse["recordSummaries"][string];
type AttributeDefinition = { id: string; name: string; type: string };

type MemberTableColumnOption = Omit<ListMemberTableColumn, "visible" | "width">;
type RowMoveDestination = Exclude<ListMemberMoveDestination, { type: "position" }>;

const defaultMemberTableColumns: ListMemberTableColumn[] = [
  {
    id: "standard:candidate:email",
    kind: "standard",
    recordType: "candidate",
    field: "email",
    label: "Candidate email",
    visible: true,
    width: 220,
  },
  {
    id: "standard:job:location",
    kind: "standard",
    recordType: "job",
    field: "location",
    label: "Job location",
    visible: true,
    width: 170,
  },
  {
    id: "standard:client:industry",
    kind: "standard",
    recordType: "client",
    field: "industry",
    label: "Client industry",
    visible: true,
    width: 170,
  },
  {
    id: "standard:contact:status",
    kind: "standard",
    recordType: "contact",
    field: "status",
    label: "Contact status",
    visible: true,
    width: 145,
  },
];

const memberIndexColumnWidth = 40;
const primaryRecordColumnWidth = 256;
const memberActionsColumnWidth = 36;

const standardFields: Record<ListRecordType, Array<{ field: string; label: string; width?: number }>> = {
  candidate: [
    { field: "email", label: "Email", width: 220 },
    { field: "phone", label: "Phone", width: 160 },
    { field: "location", label: "Location" },
    { field: "status", label: "Status", width: 145 },
    { field: "remote_id", label: "Remote ID" },
    { field: "created_at", label: "Created", width: 132 },
  ],
  job: [
    { field: "code", label: "Code", width: 120 },
    { field: "location", label: "Location" },
    { field: "salary", label: "Salary" },
    { field: "status", label: "Status", width: 145 },
    { field: "remote_id", label: "Remote ID" },
    { field: "created_at", label: "Created", width: 132 },
  ],
  client: [
    { field: "industry", label: "Industry" },
    { field: "status", label: "Status", width: 145 },
    { field: "remote_id", label: "Remote ID" },
    { field: "created_at", label: "Created", width: 132 },
  ],
  contact: [
    { field: "email", label: "Email", width: 220 },
    { field: "phone", label: "Phone", width: 160 },
    { field: "status", label: "Status", width: 145 },
    { field: "lifecycle_stage", label: "Lifecycle stage", width: 160 },
    { field: "relationship_stage", label: "Relationship stage", width: 170 },
    { field: "remote_id", label: "Remote ID" },
    { field: "created_at", label: "Created", width: 132 },
  ],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isMemberTableColumn(value: unknown): value is ListMemberTableColumn {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    (value.kind === "standard" || value.kind === "custom_attribute") &&
    listRecordTypes.includes(value.recordType as ListRecordType) &&
    typeof value.field === "string" &&
    typeof value.label === "string" &&
    typeof value.visible === "boolean" &&
    typeof value.width === "number"
  );
}

function clampColumnWidth(width: number) {
  return Math.max(96, Math.min(640, Math.round(width)));
}

export function getMemberTableColumns(meta: unknown): ListMemberTableColumn[] {
  if (!isRecord(meta) || !isRecord(meta.member_table) || !Array.isArray(meta.member_table.columns)) {
    return defaultMemberTableColumns;
  }

  const columns = meta.member_table.columns.filter(isMemberTableColumn).map((column) => ({
    ...column,
    width: clampColumnWidth(column.width),
  }));
  return columns.length ? columns : defaultMemberTableColumns;
}

export function reorderMemberTableColumns({
  columns,
  activeId,
  overId,
}: {
  columns: ListMemberTableColumn[];
  activeId: string;
  overId: string;
}) {
  const oldIndex = columns.findIndex((column) => column.id === activeId);
  const newIndex = columns.findIndex((column) => column.id === overId);
  return oldIndex < 0 || newIndex < 0 || oldIndex === newIndex ? columns : arrayMove(columns, oldIndex, newIndex);
}

function fieldOptions(recordType: ListRecordType, attributes: AttributeDefinition[]): MemberTableColumnOption[] {
  return [
    ...standardFields[recordType].map((field) => ({
      id: `standard:${recordType}:${field.field}`,
      kind: "standard" as const,
      recordType,
      field: field.field,
      label: `${entityLabels[recordType].slice(0, -1)} ${field.label}`,
    })),
    ...attributes.map((attribute) => ({
      id: `custom_attribute:${recordType}:${attribute.id}`,
      kind: "custom_attribute" as const,
      recordType,
      field: attribute.id,
      label: `${entityLabels[recordType].slice(0, -1)} · ${attribute.name}`,
    })),
  ];
}

function attributesFromSettings(value: unknown): AttributeDefinition[] {
  if (!isRecord(value) || !Array.isArray(value.attributes)) return [];
  return value.attributes.flatMap((attribute) => {
    if (!isRecord(attribute) || typeof attribute.id !== "string" || typeof attribute.name !== "string") return [];
    return [
      { id: attribute.id, name: attribute.name, type: typeof attribute.type === "string" ? attribute.type : "Text" },
    ];
  });
}

function formatCellValue(value: unknown, field: string) {
  if (value === null || value === undefined || value === "") return "";
  if (field === "created_at") {
    const date = new Date(value as string | number | Date);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
  }
  if (Array.isArray(value))
    return value
      .map((item) => formatCellValue(item, ""))
      .filter(Boolean)
      .join(", ");
  if (isRecord(value)) {
    for (const key of ["label", "name", "full_name", "title", "email", "value"]) {
      if (typeof value[key] === "string") return value[key];
    }
    return "";
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function measureCellTextWidth(cell: HTMLElement) {
  const text = cell.textContent?.trim();
  if (!text) return 0;

  const style = window.getComputedStyle(cell);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return text.length * 7 + 16;
  context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return Math.ceil(context.measureText(text).width) + 16;
}

function RecordLink({
  datasetSlug,
  entityType,
  recordId,
  onOpenCandidate,
  children,
}: {
  datasetSlug: string;
  entityType: ListRecordType;
  recordId: string;
  onOpenCandidate?: (id: string, trigger: HTMLAnchorElement) => void;
  children: ReactNode;
}) {
  const className =
    "cursor-pointer font-medium text-foreground outline-none hover:underline focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring";
  switch (entityType) {
    case "candidate":
      return (
        <Link
          to="/$datasetSlug/data/candidates/$candidateId"
          params={{ datasetSlug, candidateId: recordId }}
          className={className}
          onClick={(event) => {
            if (
              !onOpenCandidate ||
              event.button !== 0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            ) {
              return;
            }
            event.preventDefault();
            onOpenCandidate(recordId, event.currentTarget);
          }}
        >
          {children}
        </Link>
      );
    case "job":
      return (
        <Link to="/$datasetSlug/data/jobs/$jobId" params={{ datasetSlug, jobId: recordId }} className={className}>
          {children}
        </Link>
      );
    case "client":
      return (
        <Link
          to="/$datasetSlug/data/clients/$clientId"
          params={{ datasetSlug, clientId: recordId }}
          className={className}
        >
          {children}
        </Link>
      );
    case "contact":
      return (
        <Link
          to="/$datasetSlug/data/contacts/$contactId"
          params={{ datasetSlug, contactId: recordId }}
          className={className}
        >
          {children}
        </Link>
      );
  }
}

function MemberTableHeader({
  column,
  dragEnabled,
  onWidthChange,
  onWidthCommit,
}: {
  column: ListMemberTableColumn;
  dragEnabled: boolean;
  onWidthChange: (width: number) => void;
  onWidthCommit: (width: number) => void;
}) {
  const { attributes, listeners, setActivatorNodeRef, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    disabled: !dragEnabled,
  });
  const resizeStart = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  const autoFit = () => {
    const cells = Array.from(document.querySelectorAll<HTMLTableCellElement>("td[data-column-id]")).filter(
      (cell) => cell.dataset.columnId === column.id,
    );
    const contentWidth = cells.reduce((largestWidth, cell) => Math.max(largestWidth, measureCellTextWidth(cell)), 0);
    const labelWidth = column.label.length * 7 + 48;
    const width = clampColumnWidth(Math.max(contentWidth, labelWidth));
    onWidthChange(width);
    onWidthCommit(width);
  };

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (!dragEnabled) return;
    event.preventDefault();
    event.stopPropagation();
    resizeStart.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: column.width };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture is unavailable for synthetic events and some assistive technologies.
    }
    setIsResizing(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const start = resizeStart.current;
    if (!start || start.pointerId !== event.pointerId) return;
    onWidthChange(clampColumnWidth(start.startWidth + event.clientX - start.startX));
  };

  const finishResize = (event: PointerEvent<HTMLButtonElement>) => {
    const start = resizeStart.current;
    if (!start || start.pointerId !== event.pointerId) return;
    const width = clampColumnWidth(start.startWidth + event.clientX - start.startX);
    resizeStart.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    setIsResizing(false);
    onWidthChange(width);
    onWidthCommit(width);
  };

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!dragEnabled) return;
    const step = event.shiftKey ? 32 : 8;
    const width =
      event.key === "ArrowLeft"
        ? clampColumnWidth(column.width - step)
        : event.key === "ArrowRight"
          ? clampColumnWidth(column.width + step)
          : event.key === "Home"
            ? 96
            : event.key === "End"
              ? 640
              : null;
    if (width === null) return;
    event.preventDefault();
    onWidthChange(width);
    onWidthCommit(width);
  };

  return (
    <TableHead
      ref={setNodeRef}
      data-column-id={column.id}
      style={{ width: column.width, minWidth: column.width, transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "relative h-9 select-none bg-muted/35 px-2 text-[11px] font-semibold",
        isDragging && "bg-background opacity-50",
      )}
    >
      <div className="flex min-w-0 items-center gap-1">
        {dragEnabled ? (
          <Button
            ref={setActivatorNodeRef}
            type="button"
            variant="ghost"
            size="icon"
            className="size-5 shrink-0 cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
            aria-label={`Reorder ${column.label} column`}
            title="Drag to reorder. Focus and use the arrow keys to reorder."
            {...attributes}
            {...listeners}
          >
            <GripVerticalIcon className="size-3.5" />
          </Button>
        ) : null}
        <span className="truncate">{column.label}</span>
      </div>
      {dragEnabled ? (
        <button
          type="button"
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${column.label} column`}
          aria-valuemin={96}
          aria-valuemax={640}
          aria-valuenow={column.width}
          aria-valuetext={`${column.width} pixels`}
          title="Drag to resize. Double-click to fit loaded values. Use arrow keys for precise resizing."
          className={cn(
            "absolute -right-1 top-0 z-10 flex h-full w-3 touch-none cursor-col-resize items-stretch justify-center rounded-sm border-0 bg-transparent p-0 outline-none after:block after:w-px after:bg-transparent hover:after:bg-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:after:bg-primary",
            isResizing && "after:w-0.5 after:bg-primary after:shadow-[0_0_0_1px_hsl(var(--background))]",
          )}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishResize}
          onPointerCancel={finishResize}
          onDoubleClick={autoFit}
          onKeyDown={handleResizeKeyDown}
        />
      ) : null}
    </TableHead>
  );
}

export function ListMemberItem({
  index,
  member,
  summary,
  columns = defaultMemberTableColumns.filter((column) => column.visible),
  moveEnabled,
  removeEnabled,
  locked,
  datasetSlug,
  onOpenCandidate,
  onRemove,
}: {
  index: number;
  member: ListMember;
  summary?: ListRecordSummary;
  columns?: ListMemberTableColumn[];
  moveEnabled: boolean;
  removeEnabled?: boolean;
  locked: boolean;
  datasetSlug: string;
  onOpenCandidate?: (id: string, trigger: HTMLAnchorElement) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setActivatorNodeRef, setNodeRef, transform, transition, isDragging } = useSortable({
    id: member.id,
    disabled: !moveEnabled,
  });

  return (
    <TableRow
      ref={setNodeRef}
      data-member-id={member.id}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : undefined }}
      className={cn("h-10 bg-background", isDragging && "bg-muted")}
    >
      <TableCell className="w-10 px-1 text-center text-xs text-muted-foreground">
        <div className="flex items-center justify-center gap-0.5">
          {moveEnabled ? (
            <Button
              ref={setActivatorNodeRef}
              type="button"
              variant="ghost"
              size="icon"
              className="size-6 cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
              aria-label={`Reorder ${summary?.label ?? "member"}`}
              title="Drag to reorder. Focus and use the arrow keys to reorder."
              {...attributes}
              {...listeners}
            >
              <GripVerticalIcon className="size-3.5" />
            </Button>
          ) : null}
          <span className="tabular-nums">{index + 1}</span>
        </div>
      </TableCell>
      <TableCell className="min-w-64 px-2">
        <div className="flex min-w-0 items-center gap-2">
          <RecordTypeBadge entityType={member.record_type} />
          <RecordLink
            datasetSlug={datasetSlug}
            entityType={member.record_type}
            recordId={member.record_id}
            onOpenCandidate={onOpenCandidate}
          >
            <span className="block truncate text-sm">{summary?.label ?? member.record_id}</span>
          </RecordLink>
        </div>
      </TableCell>
      {columns.map((column) => {
        const value =
          column.recordType === member.record_type
            ? column.kind === "custom_attribute"
              ? summary?.customAttributes?.[column.field]
              : summary?.fields?.[column.field]
            : undefined;
        return (
          <TableCell
            key={column.id}
            data-column-id={column.id}
            style={{ width: column.width, minWidth: column.width }}
            className="max-w-0 truncate px-2 text-xs text-muted-foreground"
            title={formatCellValue(value, column.field) || undefined}
          >
            {formatCellValue(value, column.field)}
          </TableCell>
        );
      })}
      <TableCell className="w-9 px-1 text-right">
        {removeEnabled && !locked ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6 text-muted-foreground hover:text-destructive focus-visible:opacity-100 sm:opacity-0 sm:group-hover/row:opacity-100"
            aria-label={`Remove ${summary?.label ?? "member"}`}
            onClick={onRemove}
          >
            <XIcon className="size-3.5" />
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function ColumnPickerDialog({
  open,
  onOpenChange,
  columns,
  attributes,
  onChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ListMemberTableColumn[];
  attributes: Record<ListRecordType, AttributeDefinition[]>;
  onChange: (columns: ListMemberTableColumn[]) => void;
}) {
  const allOptions = useMemo(
    () => listRecordTypes.flatMap((recordType) => fieldOptions(recordType, attributes[recordType])),
    [attributes],
  );
  const columnsById = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);

  const toggleColumn = (option: MemberTableColumnOption, visible: boolean) => {
    const existing = columnsById.get(option.id);
    if (existing) {
      onChange(columns.map((column) => (column.id === option.id ? { ...column, visible } : column)));
      return;
    }
    onChange([...columns, { ...option, visible, width: option.kind === "custom_attribute" ? 180 : 160 }]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[min(44rem,calc(100vh-2rem))] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Spreadsheet columns</DialogTitle>
          <DialogDescription>
            Columns are scoped to a record type. Other member types intentionally show an empty cell.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 sm:grid-cols-2">
          {listRecordTypes.map((recordType) => {
            const options = allOptions.filter((option) => option.recordType === recordType);
            const customOptions = options.filter((option) => option.kind === "custom_attribute");
            const nativeOptions = options.filter((option) => option.kind === "standard");
            return (
              <section key={recordType} className="space-y-2">
                <h3 className="text-sm font-semibold">{entityLabels[recordType]}</h3>
                <div className="space-y-1.5">
                  {nativeOptions.map((option) => {
                    const column = columnsById.get(option.id);
                    return (
                      <ColumnPickerOption
                        key={option.id}
                        option={option}
                        column={column}
                        onToggle={toggleColumn}
                        onWidthChange={(width) =>
                          onChange(
                            columns.map((current) => (current.id === option.id ? { ...current, width } : current)),
                          )
                        }
                      />
                    );
                  })}
                </div>
                {customOptions.length ? (
                  <div className="space-y-1.5 border-t pt-2">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Custom attributes
                    </p>
                    {customOptions.map((option) => {
                      const column = columnsById.get(option.id);
                      return (
                        <ColumnPickerOption
                          key={option.id}
                          option={option}
                          column={column}
                          onToggle={toggleColumn}
                          onWidthChange={(width) =>
                            onChange(
                              columns.map((current) => (current.id === option.id ? { ...current, width } : current)),
                            )
                          }
                        />
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No custom attributes configured.</p>
                )}
              </section>
            );
          })}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ColumnPickerOption({
  option,
  column,
  onToggle,
  onWidthChange,
}: {
  option: MemberTableColumnOption;
  column?: ListMemberTableColumn;
  onToggle: (option: MemberTableColumnOption, visible: boolean) => void;
  onWidthChange: (width: number) => void;
}) {
  const id = `member-column-${option.id}`;
  return (
    <div className="flex items-center gap-2 rounded-sm px-1 py-0.5 hover:bg-muted/60">
      <Checkbox
        id={id}
        checked={column?.visible ?? false}
        onCheckedChange={(checked) => onToggle(option, checked === true)}
      />
      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer truncate text-sm">
        {option.label.replace(/^.*? · /, "")}
      </label>
      {column ? (
        <Input
          aria-label={`${option.label} width`}
          className="h-7 w-16 px-1 text-right text-xs"
          min={96}
          max={640}
          type="number"
          value={column.width}
          onChange={(event) => onWidthChange(Math.max(96, Math.min(640, Number(event.target.value) || 96)))}
        />
      ) : null}
    </div>
  );
}

export function ListMembersSpreadsheet({
  listId,
  meta,
  members,
  recordSummaries,
  datasetSlug,
  moveEnabled,
  canConfigure,
  removeEnabled,
  locked,
  onOpenCandidate,
  onMoveMember,
  onRemoveMember,
}: {
  listId: string;
  meta: unknown;
  members: ListMember[];
  recordSummaries: Record<string, ListRecordSummary>;
  datasetSlug: string;
  moveEnabled: boolean;
  canConfigure: boolean;
  removeEnabled: boolean;
  locked: boolean;
  onOpenCandidate?: (id: string, trigger: HTMLAnchorElement) => void;
  onMoveMember: (member: ListMember, destination: RowMoveDestination) => Promise<void>;
  onRemoveMember: (member: ListMember) => void;
}) {
  const [columns, setColumns] = useState(() => getMemberTableColumns(meta));
  const columnsRef = useRef(columns);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [optimisticMemberOrder, setOptimisticMemberOrder] = useState<string[] | null>(null);
  const candidateSettingsQuery = useQuery(trpc.candidatesSettings.get.queryOptions());
  const jobSettingsQuery = useQuery(trpc.jobsSettings.get.queryOptions());
  const clientSettingsQuery = useQuery(trpc.clientsSettings.get.queryOptions());
  const contactSettingsQuery = useQuery(trpc.contactsSettings.get.queryOptions());
  const saveTable = useMutation(
    trpc.lists.updateMemberTable.mutationOptions({
      onError: (error) => toast.error(error.message),
    }),
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    const nextColumns = getMemberTableColumns(meta);
    columnsRef.current = nextColumns;
    setColumns(nextColumns);
  }, [meta]);

  const attributes = useMemo<Record<ListRecordType, AttributeDefinition[]>>(
    () => ({
      candidate: attributesFromSettings(candidateSettingsQuery.data),
      job: attributesFromSettings(jobSettingsQuery.data),
      client: attributesFromSettings(clientSettingsQuery.data),
      contact: attributesFromSettings(contactSettingsQuery.data),
    }),
    [candidateSettingsQuery.data, clientSettingsQuery.data, contactSettingsQuery.data, jobSettingsQuery.data],
  );
  const visibleColumns = columns.filter((column) => column.visible);
  const tableWidth =
    memberIndexColumnWidth +
    primaryRecordColumnWidth +
    visibleColumns.reduce((width, column) => width + column.width, 0) +
    memberActionsColumnWidth;
  const orderedMembers = useMemo(() => {
    if (!optimisticMemberOrder) return members;
    const byId = new Map(members.map((member) => [member.id, member]));
    const ordered = optimisticMemberOrder.flatMap((id) => {
      const member = byId.get(id);
      return member ? [member] : [];
    });
    const orderedIds = new Set(optimisticMemberOrder);
    return [...ordered, ...members.filter((member) => !orderedIds.has(member.id))];
  }, [members, optimisticMemberOrder]);

  const persistColumns = (nextColumns: ListMemberTableColumn[]) => {
    columnsRef.current = nextColumns;
    setColumns(nextColumns);
    if (canConfigure && !locked) saveTable.mutate({ listId, columns: nextColumns });
  };

  const previewColumnWidth = (columnId: string, width: number) => {
    const nextColumns = columnsRef.current.map((column) =>
      column.id === columnId ? { ...column, width: clampColumnWidth(width) } : column,
    );
    columnsRef.current = nextColumns;
    setColumns(nextColumns);
  };

  const commitColumnWidth = (columnId: string, width: number) => {
    const nextColumns = columnsRef.current.map((column) =>
      column.id === columnId ? { ...column, width: clampColumnWidth(width) } : column,
    );
    persistColumns(nextColumns);
  };

  const handleColumnDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    persistColumns(reorderMemberTableColumns({ columns, activeId: String(active.id), overId: String(over.id) }));
  };

  const handleMemberDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !moveEnabled) return;
    const oldIndex = orderedMembers.findIndex((member) => member.id === active.id);
    const newIndex = orderedMembers.findIndex((member) => member.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const movingMember = orderedMembers[oldIndex];
    const targetMember = orderedMembers[newIndex];
    setOptimisticMemberOrder(arrayMove(orderedMembers, oldIndex, newIndex).map((member) => member.id));
    try {
      await onMoveMember(movingMember, {
        type: oldIndex < newIndex ? "after" : "before",
        target: { recordType: targetMember.record_type, recordId: targetMember.record_id },
      });
    } finally {
      setOptimisticMemberOrder(null);
    }
  };

  const columnsControl = canConfigure ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-7 shrink-0 text-xs"
      disabled={locked || saveTable.isPending}
      onClick={() => setColumnsOpen(true)}
    >
      {saveTable.isPending ? (
        <Loader2Icon className="mr-1.5 size-3.5 animate-spin" />
      ) : (
        <Settings2Icon className="mr-1.5 size-3.5" />
      )}
      Columns
    </Button>
  ) : null;

  if (members.length === 0) {
    return (
      <>
        <div className="flex min-h-44 flex-col items-center justify-center gap-2 px-4 text-center">
          <Columns3Icon className="size-5 text-muted-foreground" />
          <p className="text-sm font-medium">No visible members yet</p>
          <p className="max-w-md text-xs text-muted-foreground">
            Mixed lists can contain candidates, jobs, clients, and contacts. Add records to begin building this view.
          </p>
          {columnsControl}
        </div>
        <ColumnPickerDialog
          open={columnsOpen}
          onOpenChange={setColumnsOpen}
          columns={columns}
          attributes={attributes}
          onChange={persistColumns}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b bg-muted/20 px-3 py-2">
        <p className="text-xs text-muted-foreground">
          Drag column headers to order the sheet. Values are blank where a field does not apply to a record type.
        </p>
        {columnsControl}
      </div>
      <div className="overflow-x-auto" data-testid="list-members-spreadsheet">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            if (visibleColumns.some((column) => column.id === event.active.id)) {
              handleColumnDragEnd(event);
              return;
            }
            void handleMemberDragEnd(event);
          }}
        >
          <Table useDynamicHeight={false} className="table-fixed text-sm" style={{ width: tableWidth }}>
            <colgroup>
              <col style={{ width: memberIndexColumnWidth }} />
              <col style={{ width: primaryRecordColumnWidth }} />
              {visibleColumns.map((column) => (
                <col key={column.id} style={{ width: column.width }} />
              ))}
              <col style={{ width: memberActionsColumnWidth }} />
            </colgroup>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-9 w-10 bg-muted/35 px-1 text-center text-[11px]">#</TableHead>
                <TableHead className="h-9 min-w-64 bg-muted/35 px-2 text-[11px] font-semibold">Record</TableHead>
                <SortableContext
                  items={visibleColumns.map((column) => column.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {visibleColumns.map((column) => (
                    <MemberTableHeader
                      key={column.id}
                      column={column}
                      dragEnabled={canConfigure && !locked}
                      onWidthChange={(width) => previewColumnWidth(column.id, width)}
                      onWidthCommit={(width) => commitColumnWidth(column.id, width)}
                    />
                  ))}
                </SortableContext>
                <TableHead className="h-9 w-9 bg-muted/35" />
              </TableRow>
            </TableHeader>
            <TableBody>
              <SortableContext items={orderedMembers.map((member) => member.id)} strategy={verticalListSortingStrategy}>
                {orderedMembers.map((member, index) => (
                  <ListMemberItem
                    key={member.id}
                    index={index}
                    member={member}
                    summary={
                      recordSummaries[getListMemberKey({ recordType: member.record_type, recordId: member.record_id })]
                    }
                    columns={visibleColumns}
                    moveEnabled={moveEnabled}
                    removeEnabled={removeEnabled}
                    locked={locked}
                    datasetSlug={datasetSlug}
                    onOpenCandidate={onOpenCandidate}
                    onRemove={() => onRemoveMember(member)}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </DndContext>
      </div>
      <ColumnPickerDialog
        open={columnsOpen}
        onOpenChange={setColumnsOpen}
        columns={columns}
        attributes={attributes}
        onChange={persistColumns}
      />
    </>
  );
}
